import { type Filter, type FilterId, isMultiFilter } from '../types/filters';
import { processFilterDependencies } from './filterDependencies';
import {
  applyFilterAction,
  type FilterConsistencyState,
} from './filterGroupConsistency';
import { getFilterDefinition } from './filterRegistry';

export type MultiFilterAction =
  | {
      type: 'ADD_MULTI_FILTER_INSTANCE';
      payload: {
        parentId: FilterId;
        newInstance: Filter;
      };
    }
  | {
      type: 'UPDATE_MULTI_FILTER_INSTANCE';
      payload: {
        parentId: FilterId;
        instanceIndex: number;
        newInstance: Filter;
      };
    }
  | {
      type: 'REMOVE_MULTI_FILTER_INSTANCE';
      payload: {
        parentId: FilterId;
        instanceIndex: number;
      };
    };

export const multiFilterReducer = (
  state: FilterConsistencyState,
  action: MultiFilterAction
): FilterConsistencyState => {
  switch (action.type) {
    case 'UPDATE_MULTI_FILTER_INSTANCE': {
      const { parentId, instanceIndex, newInstance } = action.payload;
      const activeMap = state.activeKey
        ? state.groupMap.get(state.activeKey)
        : undefined;
      const existingParent = activeMap?.get(parentId);
      if (!existingParent || !isMultiFilter(existingParent)) {
        return state;
      }

      const instances = existingParent.instances;

      const nextInstances = instances.map((inst, idx) =>
        idx === instanceIndex ? newInstance : inst
      );

      const nextParent = {
        ...existingParent,
        instances: nextInstances,
        isUserSubmitted: true,
      } as typeof existingParent;

      const { nextState, updatedFilter } = applyFilterAction(
        state,
        parentId,
        nextParent,
        true
      );

      if (updatedFilter) {
        return processFilterDependencies(nextState, parentId, updatedFilter);
      }

      return nextState;
    }

    case 'ADD_MULTI_FILTER_INSTANCE': {
      const { parentId, newInstance } = action.payload;

      const activeMap = state.activeKey
        ? state.groupMap.get(state.activeKey)
        : undefined;

      const existingParent = activeMap?.get(parentId);

      const defaultDefinition = getFilterDefinition(parentId);

      if (
        !defaultDefinition ||
        !('type' in defaultDefinition) ||
        !isMultiFilter(defaultDefinition)
      ) {
        return state;
      }

      const resolvedParent =
        existingParent && isMultiFilter(existingParent)
          ? existingParent
          : defaultDefinition;

      if (!isMultiFilter(resolvedParent)) {
        return state;
      }

      const currentInstances = resolvedParent.instances ?? [];

      const nextParent = {
        ...resolvedParent,
        instances: [...currentInstances, newInstance],
        isUserSubmitted: true,
      } as typeof resolvedParent;

      const { nextState, updatedFilter } = applyFilterAction(
        state,
        parentId,
        nextParent,
        true
      );

      if (updatedFilter) {
        return processFilterDependencies(nextState, parentId, updatedFilter);
      }

      return nextState;
    }

    case 'REMOVE_MULTI_FILTER_INSTANCE': {
      const { parentId, instanceIndex } = action.payload;
      const activeMap = state.activeKey
        ? state.groupMap.get(state.activeKey)
        : undefined;
      const existingParent = activeMap?.get(parentId);

      if (!existingParent || !isMultiFilter(existingParent)) {
        return state;
      }

      const instances = existingParent.instances;

      const nextInstances = instances.filter((_, idx) => idx !== instanceIndex);

      const nextParent = {
        ...existingParent,
        instances: nextInstances,
        isUserSubmitted: true,
      } as typeof existingParent;

      const { nextState, updatedFilter } = applyFilterAction(
        state,
        parentId,
        nextParent,
        true
      );

      if (updatedFilter) {
        return processFilterDependencies(nextState, parentId, updatedFilter);
      }

      return nextState;
    }

    default:
      return state;
  }
};
